import {
  WRITE_OFF_REASON_MAX,
  formatMinor,
  lineReturnAmount,
  returnableQty,
  splitReturn,
  writeOffReasonError,
} from '@didto/domain';
import { Badge, Button, Field, IconButton, KvRow, Select, Sheet, Textarea } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useOnline } from '@/lib/network';
import type { Order, OrderDetail, OrderItem } from '@/lib/orders';
import type { PaymentMethod } from '@/lib/payments';
import { supabase } from '@/lib/supabase';
import './shift.css';

/**
 * Returns of served items on the POS (Phase 7B-2, ADR-032). The guest hands back what was served: it comes off the
 * order. What was never paid is simply no longer owed; what was paid for it goes back as money, the way it was paid or
 * in cash. Nothing goes back to stock, the kitchen or the pass (D6). Within the walk-outs' allowance the cashier
 * returns alone; beyond it the refusal names writeoff.approve and the counter asks the owner for a grant (ADR-023).
 * A return needs the server: while it cannot be reached, it cannot be confirmed (ADR-037).
 */

export interface ReturnRow {
  id: string;
  order_item_id: string;
  qty: number;
  amount_minor: number;
  refunded_minor: number;
  reason: string;
  authority: 'cashier' | 'owner';
  recorded_by_employee_id: string;
  approved_by_employee_id: string | null;
  recorded_at: string;
}

export interface OrderReturns {
  rows: ReturnRow[];
  /** Per line: units returned, and units refunded line by line (a goodwill refund naming the line). */
  returned: Record<string, number>;
  refunded: Record<string, number>;
  /** Lines prepared at a station: the tickets they are on. */
  ticketsOf: Record<string, string[]>;
}

/** Under ['orders'], so every command's refresh of the orders refreshes these too. */
export const returnKeys = {
  order: (orderId: string) => ['orders', 'returns', orderId] as const,
};

function sumBy(rows: { order_item_id: string; qty: number }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) out[r.order_item_id] = (out[r.order_item_id] ?? 0) + r.qty;
  return out;
}

export function useOrderReturns(orderId: string | null, itemIds: string[]) {
  return useQuery({
    queryKey: [...returnKeys.order(orderId ?? ''), itemIds.join(',')],
    enabled: !!orderId,
    queryFn: async (): Promise<OrderReturns> => {
      const { data, error } = await supabase
        .from('order_item_return')
        .select(
          'id, order_item_id, qty, amount_minor, refunded_minor, reason, authority, recorded_by_employee_id, approved_by_employee_id, recorded_at',
        )
        .eq('order_id', orderId ?? '')
        .order('recorded_at');
      if (error) throw error;
      const rows = (data ?? []) as ReturnRow[];
      if (itemIds.length === 0) return { rows, returned: {}, refunded: {}, ticketsOf: {} };
      const refundedRes = await supabase
        .from('reversal_item')
        .select('order_item_id, qty')
        .in('order_item_id', itemIds);
      if (refundedRes.error) throw refundedRes.error;
      const prodRes = await supabase
        .from('production_item')
        .select('order_item_id, ticket_id')
        .in('order_item_id', itemIds);
      if (prodRes.error) throw prodRes.error;
      const ticketsOf: Record<string, string[]> = {};
      for (const x of prodRes.data ?? []) (ticketsOf[x.order_item_id] ??= []).push(x.ticket_id);
      return {
        rows,
        returned: sumBy(rows),
        refunded: sumBy(refundedRes.data ?? []),
        ticketsOf,
      };
    },
  });
}

/**
 * Served, as the server reads it (app.line_served): a line prepared at a station once its ticket is served; a line
 * with nothing to prepare once it was sent or the order is handed over. The server decides.
 */
export function lineServed(item: OrderItem, detail: OrderDetail, returns: OrderReturns): boolean {
  const tickets = returns.ticketsOf[item.id];
  if (tickets && tickets.length > 0)
    return detail.tickets.some((t) => tickets.includes(t.id) && t.status === 'served');
  return (
    item.sent_at !== null ||
    detail.order.fulfilment_status === 'served' ||
    detail.order.fulfilment_status === 'not_required'
  );
}

/** Units of a served line the cashier may still return; 0 hides the action. */
export function returnableOf(item: OrderItem, detail: OrderDetail, returns: OrderReturns): number {
  const o = detail.order;
  if (o.status !== 'open' && o.status !== 'completed') return 0;
  if (item.status !== 'active' || item.line_total_minor <= 0) return 0;
  if (!lineServed(item, detail, returns)) return 0;
  return returnableQty(item.qty, returns.returned[item.id] ?? 0, returns.refunded[item.id] ?? 0);
}

/** The returns recorded on an order. */
export function ReturnsPanel({
  rows,
  items,
  currency,
  nameOf,
}: {
  rows: ReturnRow[];
  items: OrderItem[];
  currency: string;
  nameOf: (employeeId: string | null) => string;
}) {
  if (rows.length === 0) return null;
  const lineName = (id: string) => items.find((i) => i.id === id)?.name_snapshot ?? 'item';
  return (
    <div className="d-stack d-stack--tight" data-testid="returns">
      <div className="d-eyebrow">Returned</div>
      <div className="d-list">
        {rows.map((r) => (
          <div className="d-cartline" key={r.id} data-testid="return-row">
            <div className="d-cartline__main">
              <div className="d-row__title">
                {r.qty} × {lineName(r.order_item_id)}
              </div>
              <div className="d-row__meta">
                {r.refunded_minor > 0
                  ? `${formatMinor(r.refunded_minor, currency)} given back · `
                  : 'no longer owed · '}
                {r.reason} · by {nameOf(r.recorded_by_employee_id)}
                {r.approved_by_employee_id &&
                r.approved_by_employee_id !== r.recorded_by_employee_id
                  ? ` · approved by ${nameOf(r.approved_by_employee_id)}`
                  : ''}
              </div>
            </div>
            <div className="d-cartline__side">
              <strong className="d-mono">{formatMinor(r.amount_minor, currency)}</strong>
              {r.authority === 'owner' && <Badge tone="info">owner</Badge>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface ReturnInput {
  qty: number;
  reason: string;
  refund_method_id?: string;
}

export function ReturnSheet({
  item,
  order,
  returnable,
  returnedBefore,
  methods,
  currency,
  busy,
  onClose,
  onSubmit,
}: {
  item: OrderItem | null;
  order: Order | null;
  returnable: number;
  returnedBefore: number;
  methods: PaymentMethod[];
  currency: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: ReturnInput) => Promise<void>;
}) {
  return (
    <Sheet open={!!item && !!order} onClose={onClose} title="Return">
      {item && order && (
        <ReturnForm
          key={`${item.id}:${order.version}`}
          item={item}
          order={order}
          returnable={returnable}
          returnedBefore={returnedBefore}
          methods={methods}
          currency={currency}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      )}
    </Sheet>
  );
}

function ReturnForm({
  item,
  order,
  returnable,
  returnedBefore,
  methods,
  currency,
  busy,
  onClose,
  onSubmit,
}: {
  item: OrderItem;
  order: Order;
  returnable: number;
  returnedBefore: number;
  methods: PaymentMethod[];
  currency: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: ReturnInput) => Promise<void>;
}) {
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState('');
  const [touched, setTouched] = useState(false);
  const online = useOnline();
  const amount = lineReturnAmount(item.line_total_minor, item.qty, returnedBefore, qty);
  const split = splitReturn(amount, {
    totalMinor: order.total_minor,
    paidMinor: order.paid_minor,
    writtenOffMinor: order.written_off_minor,
  });
  // money goes back the way it was paid, or in cash (the server refuses any other method)
  const cash = methods.filter((m) => m.kind === 'cash' && m.enabled && !m.archived_at);
  const error =
    writeOffReasonError(reason) ??
    (qty < 1 || qty > returnable ? `At most ${returnable}` : null) ??
    (amount <= 0 ? 'Nothing to return on this line' : null);
  return (
    <div className="d-stack" data-testid="return-sheet">
      <p className="d-muted">
        The guest hands back what was served. It comes off the order; nothing goes back to stock,
        the kitchen or the pass.
      </p>
      <div className="money-subject">
        <div className="money-subject__title">
          {item.qty} × {item.name_snapshot}
        </div>
        <div className="money-subject__meta">
          on order <span className="d-mono">{order.order_number}</span>
          {returnedBefore > 0 ? ` · ${returnedBefore} returned before` : ''}
        </div>
      </div>
      {returnable > 1 && (
        <div className="d-spread">
          <span className="d-label">How many</span>
          <div className="d-qty">
            <IconButton size="sm" label="fewer" disabled={qty <= 1} onClick={() => setQty(qty - 1)}>
              −
            </IconButton>
            <span className="d-qty__value" data-testid="return-qty">
              {qty}
            </span>
            <IconButton
              size="sm"
              label="more"
              disabled={qty >= returnable}
              onClick={() => setQty(qty + 1)}
            >
              +
            </IconButton>
          </div>
        </div>
      )}
      <div className="d-kv" data-testid="return-split">
        <KvRow
          label="Off the order"
          value={<span className="d-mono">{formatMinor(amount, currency)}</span>}
        />
        {split.notOwedMinor > 0 && (
          <KvRow
            label="Not paid yet: no longer owed"
            value={<span className="d-mono">{formatMinor(split.notOwedMinor, currency)}</span>}
          />
        )}
        {split.refundMinor > 0 && (
          <KvRow
            label="Given back to the guest"
            value={<span className="d-mono">{formatMinor(split.refundMinor, currency)}</span>}
            total
            testId="return-refund"
          />
        )}
      </div>
      {split.refundMinor > 0 && cash.length > 0 && (
        <Field label="Give back by" help="Defaults to the way it was paid.">
          {(id) => (
            <Select
              id={id}
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              data-testid="return-method"
            >
              <option value="">The way it was paid</option>
              {cash.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      <Field label="Reason" help="Required; it is kept with the return and in the audit log.">
        {(id) => (
          <Textarea
            id={id}
            rows={3}
            maxLength={WRITE_OFF_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="return-reason"
          />
        )}
      </Field>
      {touched && error && (
        <p className="d-error" role="alert">
          {error}
        </p>
      )}
      <div className="d-sheet__actions">
        <Button variant="quiet" onClick={onClose}>
          Back
        </Button>
        <Button
          variant="danger-solid"
          busy={busy}
          disabled={!online}
          onClick={() => {
            setTouched(true);
            if (error) return;
            void onSubmit({
              qty,
              reason: reason.trim(),
              ...(split.refundMinor > 0 && method ? { refund_method_id: method } : {}),
            });
          }}
          data-testid="return-confirm"
        >
          Return {formatMinor(amount, currency)}
        </Button>
      </div>
    </div>
  );
}
